import Pagination from '../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FiMoreVertical } from 'react-icons/fi';
import Modal from '../../components/shared/modal';
import EditModal from './edit-modal';
import ConfirmationModal from '../../components/shared/confirmation-modal';
import { statusColors } from '../../utils/status-color-utils';
import { RiArrowUpSLine } from 'react-icons/ri';
import { RxCaretSort } from 'react-icons/rx';
import { deleteServices, updateServicesStatus } from '../../services/services-service';
import { Tooltip } from 'react-tooltip';
import { formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import Loader from '../../components/shared/loader';
import UpdateStatusModal from '../../components/shared/update-status-modal';
import AuditLogModal from '../../components/shared/AuditLogModal';
import { camelCaseToWords } from '../../utils/structure-words-utils';

const ServicesTables = ({
  data,
  setData,
  setSelectedRows, selectedRows,
  globalFilter,
  onGlobalFilterChange,
  newlyAddedItem,
  setNewlyAddedItem,
  onClearNewlyAddedItem,
  fetchServices,
  loadingData,
  accountId,
  vendors,
  vehicles,
  types,
  statusOptions,
  fetchGlobalData,
  globalDataloading
}) => {
  const [sorting, setSorting] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isAuditLogModalOpen, setIsAuditLogModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (newlyAddedItem) {
      setSelectedRow(newlyAddedItem);
      setIsEditModalOpen(true);
      setNewlyAddedItem(null);
    }
  }, [newlyAddedItem, setNewlyAddedItem]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleActionClick = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
    fetchGlobalData();
  };

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  const handleRowClick = (row, e) => {
    if (!e.target.closest('input[type="checkbox"]')) {
      setSelectedRow(row.original);
      setIsEditModalOpen(true);
      fetchGlobalData();
    }
  };

  const handleConfirmAction = (action, row) => {
    setSelectedRow(row.original);
    if (action === 'Change Status') {
      setIsUpdateStatusModalOpen(true);
    } else {
      setConfirmAction(action);
      setIsConfirmModalOpen(true);
    }
    setErrorMessage(null);
    closeMenu();
  };

  const handleConfirm = async () => {
    if (!selectedRow || !confirmAction) return;

    if (confirmAction === 'delete') {
      try {
        setIsDeleting(true);
        const serviceId = selectedRow.serviceId;
        const res = await deleteServices(accountId, serviceId);

        if (res.status === 204) {
          setData(prevData => prevData.filter(item => item.serviceId !== serviceId));
          setIsConfirmModalOpen(false);
          setSelectedRow(null);
          setConfirmAction(null);
          setSuccessMessage('Service deleted successfully!');
        } else {
          setErrorMessage(res.data?.message || 'Failed to delete Service entry');
        }
      } catch (error) {
        console.error('Error deleting Service:', error);
        setErrorMessage('An error occurred while deleting the Service entry');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const formatTypeName = (type) => {
    return type
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="flex justify-center w-full">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.getToggleAllRowsSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center w-full" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                row.getToggleSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        enableSorting: false,
        size: 50,
        maxSize: 50,
      },
      {
        accessorKey: 'vehicle.name',
        header: 'Vehicle',
        enableSorting: true,
        size: 120,
        maxSize: 200,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'vendor.name',
        header: 'Vendor',
        enableSorting: true,
        size: 120,
        maxSize: 150,
        cell: (info) => <span className="truncate">{info.getValue() || '----'}</span>,
      },
      {
        accessorKey: 'types',
        header: 'Type',
        enableSorting: true,
        size: 200,
        maxSize: 250,
        accessorFn: row => Array.isArray(row.types) ? row.types.join(', ') : row.types,
        cell: (info) => {
          const types = info.row.original.types;
          if (!types) return null;
          if (!Array.isArray(types)) return <span className="truncate">{types}</span>;
          if (types.length === 0) return null;

          return (
            <div className="flex flex-wrap gap-1 max-w-[200px]">
              {types.map((type, index) => {
                const formattedType = formatTypeName(type);
                return (
                  <span
                    key={index}
                    className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 truncate"
                    title={formattedType}
                  >
                    {formattedType}
                  </span>
                );
              })}
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const types = row.original.types;
          if (!Array.isArray(types)) return false;
          return types.some(type =>
            type.toLowerCase().includes(filterValue.toLowerCase())
          );
        },
      },
      {
        accessorKey: 'serviceTime',
        header: 'Service Time',
        enableSorting: true,
        size: 150,
        maxSize: 180,
        cell: (info) => <span className="truncate">{formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a')}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        size: 100,
        maxSize: 120,
        cell: info => {
          const rawStatus = info.getValue();
          const status = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : 'unknown';
          const color = statusColors[status] || statusColors.default;
          const displayStatus = camelCaseToWords(info.getValue());
          return (
            <div className="flex justify-start">
              <span
                className="py-0.5 rounded-md text-sm font-medium capitalize text-center whitespace-nowrap"
                style={{
                  backgroundColor: color,
                  color: 'black',
                  border: `1px solid ${color}`,
                  width: '100px',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
              >
                {displayStatus}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'note',
        header: 'Note',
        enableSorting: true,
        size: 150,
        maxSize: 200,
        cell: (info) => {
          const note = info.getValue() || '';
          const excerpt = note.length > 20 ? `${note.substring(0, 20)}...` : note;
          const tooltipId = `tooltip-${info.row.id}`;

          return (
            <>
              <span
                className="cursor-pointer truncate"
                data-tooltip-id={tooltipId}
                data-tooltip-content={note}
                data-tooltip-place="top"
              >
                {excerpt}
              </span>
              <Tooltip
                id={tooltipId}
                place="top"
                className="z-50"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  padding: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: '#000d',
                  color: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                }}
              />
            </>
          );
        },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => {
          const rowId = row.id;
          return (
            <div className=" flex justify-center w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(rowId);
                }}
                className="p-1 rounded-full hover:bg-gray-200"
              >
                <FiMoreVertical className="text-gray-600 text-sm" />
              </button>
              {openMenuId === rowId && (
                <div
                  className="absolute right-0 w-32 bg-white rounded-md shadow-md z-10 border border-gray-200 top-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('Change Status', row)}
                  >
                    Change Status
                  </button> */}
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setSelectedRow(row.original);
                      setIsAuditLogModalOpen(true);
                      closeMenu();
                    }}
                  >
                    Audit Log
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-red-600 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('delete', row)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 50,
        // maxSize: 50,
      },
    ],
    [openMenuId]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      rowSelection: selectedRows,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableSortingRemoval: false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onRowSelectionChange: setSelectedRows,
    globalFilterFn: (row, columnId, filterValue) => {
      const searchLower = filterValue.toLowerCase();
      const rowValue = String(row.getValue(columnId)).toLowerCase();

      if (columnId === 'serviceTime') {
        return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
      } else if (columnId.includes('.')) {
        const [parent, child] = columnId.split('.');
        const value = row.original[parent]?.[child] || '';
        return String(value).toLowerCase().includes(searchLower);
      }

      return rowValue.includes(searchLower);
    },
  });

  return (
    <div className="w-full">
      {successMessage && (
        <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
          {errorMessage}
        </div>
      )}

      <div className="overflow-x-auto max-w-full pb-16">
        {loadingData ? (
          <Loader />
        ) : (
          <table className="min-w-full text-sm border border-gray-200" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '50px', maxWidth: '50px' }} />
              <col style={{ width: '120px', maxWidth: '200px' }} />
              <col style={{ width: '120px', maxWidth: '150px' }} />
              <col style={{ width: '200px', maxWidth: '250px' }} />
              <col style={{ width: '150px', maxWidth: '180px' }} />
              <col style={{ width: '100px', maxWidth: '120px' }} />
              <col style={{ width: '150px', maxWidth: '200px' }} />
              <col style={{ minWidth: '50px' }} />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-2 py-1.5 border-b capitalize truncate"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        width: header.column.getSize(),
                        maxWidth: header.column.columnDef.maxSize,
                      }}
                    >
                      <div className="flex items-center gap-1 truncate">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-black">
                            {header.column.getIsSorted() === 'asc' ? (
                              <RiArrowUpSLine className="text-base mt-0.5" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <RiArrowUpSLine className="text-base rotate-180" />
                            ) : (
                              <RxCaretSort className="text-lg" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table
                .getRowModel()
                .rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 cursor-pointer relative"
                    onClick={(e) => handleRowClick(row, e)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-2 py-1 border-b truncate"
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isEditModalOpen && selectedRow}
        onClose={() => setIsEditModalOpen(false)}
      >
        <EditModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setIsEditModalOpen={setIsEditModalOpen}
          setData={setData}
          vendors={vendors}
          vehicles={vehicles}
          types={types}
          globalDataloading={globalDataloading}
        />
      </Modal>

      <Modal
        isOpen={isUpdateStatusModalOpen && selectedRow}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:!w-[25%] lg:!w-[28%] md:!w-[35%] sm:!w-[45%] !w-[90%] !px-0"
      >
        <UpdateStatusModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setData={setData}
          setIsUpdateStatusModalOpen={setIsUpdateStatusModalOpen}
          fetchData={fetchServices}
          accountId={accountId}
          modalTitle="Change Service Status"
          statusKey="status"
          statusOptions={statusOptions}
          updateStatusApi={updateServicesStatus}
          uniqueIdKey="serviceId"
        />
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen && selectedRow}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setErrorMessage(null);
          setSelectedRow(null);
        }}
        modalClass="xl:!w-[28%] lg:!w-[35%] md:w-[40%] sm:w-[60%] w-[90%]"
      >
        <ConfirmationModal
          confirmAction={confirmAction}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          handleConfirm={handleConfirm}
          loading={isDeleting}
          errorMessage={errorMessage}
        />
      </Modal>
      <Modal
        isOpen={isAuditLogModalOpen && selectedRow}
        onClose={() => {
          setIsAuditLogModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:w-[60%] lg:w-[70%] md:w-[80%] sm:w-[90%] w-[95%]"
      >
        <AuditLogModal
          isOpen={isAuditLogModalOpen}
          onClose={() => {
            setIsAuditLogModalOpen(false);
            setSelectedRow(null);
          }}
          accountId={accountId}
          linkedType="service"
          linkedId={selectedRow?.serviceId}
        />
      </Modal>

      <Pagination
        currentPage={currentPage}
        totalItems={table.getFilteredRowModel().rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        }}
      />
    </div >
  );
};

export default ServicesTables;